/**
 * Google Analytics, on the terms the privacy policy commits to.
 *
 * Two separate problems, both solved here rather than at the call site.
 *
 * FIRST, WHOSE TRAFFIC THIS IS. This bundle is deployed twice:
 * customer.safirios.com is the SafiriOS demo, and customer.rileycourier.com is
 * where Riley's real customers track real consignments. Riley's traffic is a
 * client's, covered by Riley's terms and not by safirios.com/privacy, so it is
 * not ours to send anywhere. The host check allows one domain rather than
 * blocking the other, so a client onboarded tomorrow is silent by default
 * instead of silently reporting.
 *
 * SECOND, WHAT IS IN A URL. safirios.com/privacy says of the Platform: "It does
 * not receive the contents of your operation: no order references, customer or
 * driver names, addresses, documents, or any other operational data are sent to
 * it." A default gtag install breaks that on its first page view -- `/track/1129`
 * is one real consignment, and the document title carries the customer's own
 * company name. So automatic page views are off, and each view reports the
 * ROUTE Vue Router matched: `to.matched`'s last entry is the path pattern as it
 * was declared in router/index.ts, `/track/:id`, which is literal text from our
 * own source. No list to keep in step, and no value can travel in it.
 *
 * The record itself -- which order -- travels BESIDE the path, as an event
 * parameter drawn from the route's own params. That split is not a compromise:
 * GA4 reports are pre-aggregated, and a path with thousands of distinct values
 * collapses into an `(other)` bucket that answers nothing, while the same value
 * as a parameter stays queryable. What never travels is a credential (see
 * SECRET_PARAM) or a person's name, email, phone or address -- Google's terms
 * prohibit those, so identity is sent as OUR ids, which only we can resolve.
 *
 * With no measurement id configured nothing happens at all: no script, no
 * cookie, no request.
 */
import type { Router, RouteLocationNormalized } from 'vue-router';

const MEASUREMENT_ID = ((import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) ?? '').trim();

/**
 * The one domain whose traffic belongs to SafiriOS. The leading dot matters: it
 * matches subdomains and cannot be satisfied by a lookalike registration such
 * as `notsafirios.com`.
 */
const OWN_DOMAIN = '.safirios.com';

export function hostAllowed(hostname: string): boolean {
  return hostname.toLowerCase().endsWith(OWN_DOMAIN);
}

function enabled(): boolean {
  return (
    MEASUREMENT_ID !== '' &&
    typeof window !== 'undefined' &&
    hostAllowed(window.location.hostname)
  );
}

/**
 * The route a navigation landed on, as declared. Vue Router keeps the matched
 * records on the destination, so the pattern is available without us repeating
 * the route table anywhere.
 */
export function screenOf(to: RouteLocationNormalized): string {
  const last = to.matched[to.matched.length - 1];
  const path = last?.path ?? '';
  if (path !== '') return path;
  // Nothing matched -- a 404. The path is unexplained, which is exactly where a
  // real order reference would hide, so none of it is passed on.
  return to.path
    .split('/')
    .map((part) => (part === '' ? part : /^\d+$/.test(part) ? ':id' : ':seg'))
    .join('/') || '/';
}

/**
 * Parameter names whose VALUE is a credential. Matched by NAME, on the way out,
 * so a route declared later cannot leak one by being forgotten about.
 */
const SECRET_PARAM = /token|password|secret|passwd|otp|pin|key|signature|nonce/i;

/** GA4 caps a parameter value at 100 characters. */
const MAX_VALUE = 100;

/**
 * The record a navigation named, as a TYPE and an ID rather than a parameter
 * named after the route. `/track/1129` reports
 * `record_type: 'track', record_id: '1129'`.
 *
 * Four names in total across every app, which matters because GA4 allows 50
 * event-scoped custom dimensions for the WHOLE property; a name per route would
 * eat that budget for one app. It also reports better: "views by record_type"
 * is a question you can actually ask.
 */
export function routeParams(to: RouteLocationNormalized): Record<string, string> {
  const out: Record<string, string> = {};
  const pattern = to.matched[to.matched.length - 1]?.path ?? '';
  let type = 'page';
  let seen = 0;

  for (const segment of pattern.split('/')) {
    if (segment === '') continue;
    if (!segment.startsWith(':')) {
      type = segment.replace(/[^A-Za-z0-9]+/g, '_');
      continue;
    }
    // Vue spells optional and repeatable params `:id?` and `:id+`.
    const name = segment.slice(1).replace(/[?*+]$/, '');
    seen += 1;
    const raw = (to.params ?? {})[name];
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (typeof value !== 'string' || value === '') continue;
    if (SECRET_PARAM.test(name)) continue;
    const prefix = seen === 1 ? 'record' : 'sub';
    out[`${prefix}_type`] = type;
    out[`${prefix}_id`] = value.slice(0, MAX_VALUE);
  }
  return out;
}

/** Who is using the portal, as ids we can resolve and Google cannot. */
export interface Identity {
  userId?: string | number | null;
  role?: string | null;
  /** Which customer company this person belongs to, by id and never by name. */
  accountId?: string | number | null;
}

/**
 * Say who this is. Call on sign-in and on sign-out (with `{}`), so that on a
 * shared machine the next person is not filed under the last one.
 */
export function identify(who: Identity): void {
  if (!enabled()) return;
  start();
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  if (typeof w.gtag !== 'function') return;
  w.gtag('set', { user_id: who.userId === null || who.userId === undefined ? null : String(who.userId) });
  w.gtag('set', 'user_properties', {
    user_role: who.role ? String(who.role).slice(0, MAX_VALUE) : null,
    account_id: who.accountId === null || who.accountId === undefined ? null : String(who.accountId).slice(0, MAX_VALUE),
  });
}

/**
 * Make the route the default for EVERY event on this page, not just ours.
 *
 * GA4's Enhanced Measurement (scroll, outbound click, form_start, video, file
 * download) is generated inside gtag.js, which reads `document.location` and
 * `document.title` directly. Those events never see the parameters we put on
 * our own page_view, so a scroll on /track/1129 was sending the real URL and
 * the real title -- the exact thing the rest of this file exists to prevent.
 * Proved with a real browser: an `en=scroll` request carried the raw URL and
 * "Client Portal" while our page_view alongside it carried the route.
 *
 * `gtag('set', ...)` sets defaults that all subsequent events inherit, so this
 * closes the hole in code rather than depending on the Enhanced Measurement
 * toggle staying switched off in the GA console.
 */
function setScreen(path: string): void {
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  if (typeof w.gtag !== 'function') return;
  w.gtag('set', {
    page_location: window.location.origin + path,
    page_title: path,
  });
}

let started = false;

/** Load gtag once. Safe to call more than once. */
function start(): void {
  if (started) return;
  started = true;

  const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...a: unknown[]) => void };
  w.dataLayer = w.dataLayer ?? [];
  // MUST push the `arguments` object, not an array of the same values.
  // gtag.js distinguishes the two and silently ignores a real Array: the queue
  // fills, gtag.js loads, and nothing is ever sent. Cost an hour; verified with
  // a real browser, which is the only way this shows up.
  // eslint-disable-next-line prefer-rest-params
  w.gtag = function gtag() { w.dataLayer?.push(arguments); };
  w.gtag('js', new Date());
  // send_page_view false is the whole point: the automatic one would carry the
  // raw URL and the document title.
  w.gtag('config', MEASUREMENT_ID, { send_page_view: false });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(s);
}

/**
 * Report one view per navigation, for as long as this deployment is allowed to.
 * Call once, with the router, at start-up.
 */
export function installAnalytics(router: Router): void {
  if (!enabled()) return;
  start();
  // The first screen, before the router has navigated: resolve() gives the
  // matched route synchronously, so an Enhanced Measurement event that fires
  // before the first afterEach still inherits a route rather than the real URL.
  try {
    setScreen(screenOf(router.resolve(window.location.pathname) as RouteLocationNormalized));
  } catch {
    setScreen('/');
  }

  router.afterEach((to) => {
    const w = window as unknown as { gtag?: (...a: unknown[]) => void };
    if (typeof w.gtag !== 'function') return;
    const screen = screenOf(to);
    // Re-set on every navigation: a scroll after the third screen must not
    // report the first one, nor the real URL.
    setScreen(screen);
    w.gtag('event', 'page_view', {
      page_path: screen,
      // Explicitly the route, never document.title, which carries the
      // customer's own company name.
      page_title: screen,
      page_location: window.location.origin + screen,
      ...routeParams(to),
    });
  });
}
